import { getContext, put, call, takeEvery } from 'redux-saga/effects';
import { SubmissionError } from 'redux-form';
import { SagaContext } from 'shared/types/common';
import { Services } from 'init/services';
import { getFormErrors } from 'shared/utils/formatError';
import { actions } from 'actions';
import { ApolloError, MutationOptions } from 'apollo-client';
import { routine } from './actions';
import { FormData } from './constraints';
import { Selector } from './selectors';
import { Props } from './index';
import * as mutation from 'mutations/CreateIdeaBuyOrder/mutation.gql';
import { orderListQuery } from 'queries/OrderList';
import { mainAccountQuery } from 'shared/queries/MainAccountsQuery/builder';
import * as dataLayer from 'shared/utils/gtmDataLayer';
import {
  CreateIdeaBuyOrderMutation,
  CreateIdeaBuyOrderMutationVariables,
} from 'mutations/CreateIdeaBuyOrder/__generated__/CreateIdeaBuyOrderMutation';

interface FormAction {
  payload: {
    values: FormData;
    props: Props & Selector;
  };
  type: typeof routine.TRIGGER;
}

// @translate
type Errors =
  | 'Investment idea has expired FinishRequestDate'
  | 'Start date of idea expired'
  | 'Order count threshold reached for selected idea';

export default function* () {
  const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
    SagaContext.ApolloClient
  );

  yield takeEvery(
    routine.TRIGGER,
    function* ({
      payload: {
        values,
        props: { idea },
      },
    }: FormAction) {
      const options: MutationOptions<
        CreateIdeaBuyOrderMutation,
        CreateIdeaBuyOrderMutationVariables
      > = {
        mutation,
        variables: {
          request: {
            amount: {
              value: +values.amount,
              currency: idea.enterAmount.currency,
            },
            ideaId: idea.id,
          },
        },
        refetchQueries: [orderListQuery({ archived: false }), mainAccountQuery()],
        awaitRefetchQueries: true,
      };

      try {
        yield call((opt: typeof options) => apolloClient.mutate(opt), options);
        yield put(routine.success());

        dataLayer.push({
          eventCategory: 'form',
          eventAction: 'orderSubmit',
          eventLabel: `investments/${idea.id}`,
          eventValue: values.amount,
        });

        yield put(actions.showOrderSubmittedModal(idea.ideaType));
      } catch (e: ApolloError | any) {
        const errors = getFormErrors(e, (fieldName) =>
          fieldName === 'requestAmount' ? 'amount' : undefined
        );
        yield put(routine.failure(new SubmissionError<FormData, Errors>(errors)));
      }
    }
  );
}
